import { Link } from "react-router-dom";
import { IconClipboard, IconConcept, IconLocation } from "../components/ClaimFormIcons";

export type SelfRegistrationGateReason = "unknown" | "inactive" | "not_self_registration";

interface SelfRegistrationGatePageProps {
  reason: SelfRegistrationGateReason;
  storeIdHint: string;
  /** Catalog / display concept when known from lookup */
  conceptLabel?: string | null;
  /** Catalog location or site name when known */
  locationLabel?: string | null;
}

const SUPPORT_SNIPPET = (
  storeId: string,
  reason: SelfRegistrationGateReason,
  concept: string | null,
  location: string | null
) =>
  [
    "Coconut Telegraph self-registration",
    concept ? `Concept: ${concept}` : null,
    location ? `Location: ${location}` : null,
    `Reference ID: ${storeId || "—"}`,
    `Issue: ${reason}`,
    "I expected this link to work for requesting an account.",
  ]
    .filter(Boolean)
    .join("\n");

export default function SelfRegistrationGatePage({
  reason,
  storeIdHint,
  conceptLabel,
  locationLabel,
}: SelfRegistrationGatePageProps) {
  const title =
    reason === "unknown"
      ? "We couldn’t find this location"
      : reason === "inactive"
        ? "This location is not active"
        : "Self-registration unavailable for this location";

  const body =
    reason === "unknown"
      ? "Double-check the link you were sent, or choose a location from the general request form to get started."
      : reason === "inactive"
        ? "This store is no longer active in our directory. Please contact your administrator for the correct link or next steps."
        : "Self-registration isn’t enabled for this location. Please contact your administrator for access or use the general request form to get started.";

  const callout =
    reason === "unknown"
      ? "We couldn’t verify this location from your link."
      : reason === "inactive"
        ? "This location is not active in our system."
        : "Self-registration is disabled for this location.";

  const conceptDisplay = conceptLabel?.trim() || "—";
  const locationDisplay = locationLabel?.trim() || "—";
  const referenceDisplay = storeIdHint.trim() || "—";

  const copySupport = () => {
    void navigator.clipboard.writeText(
      SUPPORT_SNIPPET(storeIdHint, reason, conceptLabel?.trim() || null, locationLabel?.trim() || null)
    );
  };

  return (
    <div className="claim-layout claim-form-page">
      <header className="claim-hero" aria-label="Coconut Telegraph" />

      <div className="claim-body">
        <div className="claim-card claim-card--wide claim-gate-card">
          <div className="claim-gate">
            <div className="claim-gate-header">
              <div className="claim-gate-header-icon" aria-hidden>
                <span className="claim-gate-header-icon-inner">🌴</span>
              </div>
              <div className="claim-gate-header-text">
                <h2 className="claim-gate-title">{title}</h2>
                <div className="claim-gate-title-underline" aria-hidden />
              </div>
            </div>

            <p className="claim-gate-lead">{body}</p>

            <div className="claim-gate-callout" role="status">
              <span className="claim-gate-callout-icon" aria-hidden>
                i
              </span>
              <span className="claim-gate-callout-text">{callout}</span>
            </div>

            <div className="claim-gate-details" role="group" aria-label="Location details">
              <div className="claim-gate-detail-row">
                <div className="claim-gate-detail-icon" aria-hidden>
                  <IconConcept />
                </div>
                <div className="claim-gate-detail-main">
                  <div className="claim-gate-detail-label">Concept</div>
                  <div className="claim-gate-detail-value">{conceptDisplay}</div>
                </div>
              </div>
              <div className="claim-gate-detail-row">
                <div className="claim-gate-detail-icon" aria-hidden>
                  <IconLocation />
                </div>
                <div className="claim-gate-detail-main">
                  <div className="claim-gate-detail-label">Location</div>
                  <div className="claim-gate-detail-value">{locationDisplay}</div>
                </div>
              </div>
              <div className="claim-gate-detail-row">
                <div className="claim-gate-detail-icon" aria-hidden>
                  <IconClipboard />
                </div>
                <div className="claim-gate-detail-main">
                  <div className="claim-gate-detail-label">Reference ID</div>
                  <div className="claim-gate-detail-value claim-gate-detail-value--mono">{referenceDisplay}</div>
                </div>
              </div>
            </div>

            <div className="claim-gate-actions">
              <Link to="/form" className="claim-gate-btn-primary">
                <span>Request access another way</span>
                <span className="claim-gate-btn-chevron" aria-hidden>
                  ›
                </span>
              </Link>
              <button type="button" className="claim-gate-btn-secondary" onClick={copySupport}>
                <span className="claim-gate-btn-secondary-icon" aria-hidden>
                  <IconClipboard />
                </span>
                <span>Copy details for support</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
